import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_URL from '../../config/api';

const RecordsList = () => {
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    production_line: '',
    model: '',
    retouch_type: '',
    date_from: '',
    date_to: ''
  });
  const [lines, setLines] = useState([]);
  const [models, setModels] = useState([]);
  const [retouches, setRetouches] = useState([]);

  const fetchData = useCallback(async () => {
    try {
      const [linesRes, modelsRes, retouchesRes] = await Promise.all([
        axios.get(`${API_URL}/api/production-lines/`),
        axios.get(`${API_URL}/api/models/`),
        axios.get(`${API_URL}/api/retouch-types/`)
      ]);
      setLines(linesRes.data.results || linesRes.data);
      setModels(modelsRes.data.results || modelsRes.data);
      setRetouches(retouchesRes.data.results || retouchesRes.data);
    } catch (error) {
      console.error('Erreur:', error);
    }
  }, []);

  const fetchRecords = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      Object.keys(filters).forEach(key => {
        if (filters[key]) {
          params.append(key, filters[key]);
        }
      });
      const response = await axios.get(`${API_URL}/api/quality-records/?${params}`);
      setRecords(response.data.results || response.data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur:', error);
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchRecords();
  }, [fetchRecords]);

  const clearFilters = () => {
    setFilters({
      production_line: '',
      model: '',
      retouch_type: '',
      date_from: '',
      date_to: ''
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner h-12 w-12"></div>
      </div>
    );
  }

  const hasActiveFilters = Object.values(filters).some(v => v !== '');

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header amélioré */}
      <div className="bg-gradient-primary rounded-xl shadow-corporate-xl p-6 sm:p-8 text-white">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h2 className="text-2xl sm:text-3xl font-bold mb-2">Enregistrements</h2>
            <p className="text-white/80 text-sm sm:text-base">Consultez tous les enregistrements de contrôle qualité</p>
          </div>
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="btn-secondary bg-white/10 hover:bg-white/20 border-white/30 text-white"
            >
              Réinitialiser les filtres
            </button>
          )}
        </div>
      </div>

      {/* Filtres améliorés */}
      <div className="card border-0 shadow-corporate-lg">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-bold text-corporate-900 flex items-center space-x-2">
            <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
            </svg>
            <span>Filtres de recherche</span>
          </h3>
          {hasActiveFilters && (
            <span className="px-3 py-1 bg-primary-100 text-primary-700 rounded-lg text-xs font-semibold">
              {Object.values(filters).filter(v => v !== '').length} filtre{Object.values(filters).filter(v => v !== '').length > 1 ? 's' : ''} actif{Object.values(filters).filter(v => v !== '').length > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
          <select
            value={filters.production_line}
            onChange={(e) => setFilters({ ...filters, production_line: e.target.value })}
            className="input-modern"
          >
            <option value="">Toutes les chaînes</option>
            {lines.map(line => (
              <option key={line.id} value={line.id}>{line.name}</option>
            ))}
          </select>
          
          <select
            value={filters.model}
            onChange={(e) => setFilters({ ...filters, model: e.target.value })}
            className="input-modern"
          >
            <option value="">Tous les modèles</option>
            {models.map(model => (
              <option key={model.id} value={model.id}>{model.name}</option>
            ))}
          </select>
          
          <select
            value={filters.retouch_type}
            onChange={(e) => setFilters({ ...filters, retouch_type: e.target.value })}
            className="input-modern"
          >
            <option value="">Tous les types</option>
            {retouches.map(retouch => (
              <option key={retouch.id} value={retouch.id}>{retouch.name}</option>
            ))}
          </select>
          
          <input
            type="date"
            value={filters.date_from}
            onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
            className="input-modern"
            placeholder="Date début"
          />
          
          <input
            type="date"
            value={filters.date_to}
            onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
            className="input-modern"
            placeholder="Date fin"
          />
        </div>
      </div>

      {/* Table améliorée */}
      {records.length === 0 ? (
        <div className="card text-center py-16 border-0 shadow-corporate-lg">
          <div className="w-20 h-20 bg-gradient-primary rounded-full flex items-center justify-center mx-auto mb-4 text-4xl">
            📋
          </div>
          <h3 className="text-xl font-bold text-corporate-900 mb-2">Aucun enregistrement</h3>
          <p className="text-gray-600 mb-6">Aucun enregistrement ne correspond aux filtres sélectionnés</p>
          {hasActiveFilters && (
            <button onClick={clearFilters} className="btn-primary">
              Réinitialiser les filtres
            </button>
          )}
        </div>
      ) : (
        <div className="card border-0 shadow-corporate-lg overflow-hidden p-0">
          <div className="overflow-x-auto">
            <table className="w-full" style={{ tableLayout: 'auto', width: '100%' }}>
              <thead>
                <tr className="bg-gradient-primary text-white">
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" style={{ width: '15%', minWidth: '150px' }}>Date/Heure</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" style={{ width: '12%', minWidth: '120px' }}>Chaîne</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" style={{ width: '25%', minWidth: '200px' }}>Modèle</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" style={{ width: '23%', minWidth: '180px' }}>Type de retouche</th>
                  <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" style={{ width: '15%', minWidth: '130px' }}>Ouvrier</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-100">
                {records.map((record, index) => (
                  <tr
                    key={record.id}
                    className="hover:bg-primary-50/50 transition-colors duration-200 border-b border-gray-100"
                    style={{ animationDelay: `${index * 0.01}s` }}
                  >
                    <td className="px-4 py-3 text-sm text-gray-900 align-top" style={{ width: '15%', minWidth: '150px' }}>
                      <div className="flex flex-col">
                        <span className="font-semibold text-gray-900">
                          {new Date(record.recorded_at).toLocaleDateString('fr-FR', { 
                            day: '2-digit', 
                            month: 'short', 
                            year: 'numeric' 
                          })}
                        </span>
                        <span className="text-xs text-gray-500 mt-0.5">
                          {new Date(record.recorded_at).toLocaleTimeString('fr-FR', { 
                            hour: '2-digit', 
                            minute: '2-digit' 
                          })}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 align-top" style={{ width: '12%', minWidth: '120px' }}>
                      <span className="badge-info">{record.production_line_name || 'N/A'}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 align-top" style={{ width: '25%', minWidth: '200px' }}>
                      <span className="font-semibold text-corporate-900">{record.model_name || 'N/A'}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 align-top" style={{ width: '23%', minWidth: '180px' }}>
                      <span>{record.retouch_type_name || 'N/A'}</span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 align-top" style={{ width: '15%', minWidth: '130px' }}>
                      <div className="flex items-center space-x-2">
                        <div className="w-7 h-7 bg-corporate-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <svg className="w-3.5 h-3.5 text-corporate-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                        </div>
                        <span className="text-gray-700 truncate">{record.worker_username || 'Anonyme'}</span>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 sm:px-6 py-4 border-t border-gray-200">
            <div className="flex flex-col sm:flex-row justify-between items-center gap-2">
              <p className="text-sm font-semibold text-gray-700">
                <span className="text-primary-600">{records.length}</span> enregistrement{records.length > 1 ? 's' : ''} trouvé{records.length > 1 ? 's' : ''}
              </p>
              {hasActiveFilters && (
                <button
                  onClick={clearFilters}
                  className="text-xs text-primary-600 hover:text-primary-700 font-semibold underline"
                >
                  Réinitialiser les filtres
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecordsList;
